import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SelectionInputs, SelectionQuota, SelectionResult } from "../../types";
import {
  fetchSelectionQuota,
  previewSelection,
  recommendSelection,
} from "../../utils/api";
import { AppHeader, Banner, ChoiceRow, Field, Icon, SectionHead } from "../common/ui";
import ResultScreen, { MixCard } from "./ResultScreen";

export interface SelectionWizardCache {
  result: SelectionResult | null;
  inputs: SelectionInputs;
  preview?: SelectionResult | null;
  step?: number;
  schemeGroup?: string | null;
}

interface Props {
  token: string;
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
  onOpenLesson?: (lessonId: string) => void;
  onOpenPlans?: () => void;
  cached?: SelectionWizardCache | null;
  onCache?: (cache: SelectionWizardCache | null) => void;
}

const TOTAL_STEPS = 5;
const EYE_SWATCH: Record<string, string> = {
  brown: "#6b4327", copper_brown: "#9a5b2e", green: "#5f8a5a", blue: "#5a86c4", gray: "#9aa3ad",
};
const EYE_IMG: Record<string, string> = {
  brown: "/kb/select/eyes_brown.jpg", copper_brown: "/kb/select/eyes_brown.jpg", green: "/kb/select/eyes_green.jpg",
  blue: "/kb/select/eyes_blue.jpg", gray: "/kb/select/eyes_grey.jpg",
};
const SKIN_IMG: Record<string, string> = {
  fair: "/kb/select/skin_fair.jpg", light: "/kb/select/skin_light.jpg",
  olive: "/kb/select/skin_olive.jpg", tan: "/kb/select/skin_tan.jpg",
  dark: "/kb/select/skin_dark.jpg",
};
const COUPEROSE_IMG = "/kb/select/couperose.jpg";
const HAIR_IMG: Record<string, string> = {
  red: "/kb/select/hair_red.jpg", blonde: "/kb/select/hair_blonde.jpg",
  brown: "/kb/select/hair_light_brown.jpg", chestnut: "/kb/select/hair_brown.jpg",
  black: "/kb/select/hair_dark.jpg",
};
const PMU_SWATCH: Record<string, string> = {
  gray: "#8a8a8a", green: "#8a9a4a", yellow: "#c9a93c", orange: "#d07030",
  salmon: "#e08a6a", red: "#b05050", violet: "#8a6aa0",
};
const PMU_COLORS = ["none", ...Object.keys(PMU_SWATCH)];
const IRIS_MARKERS = ["none", "green", "yellow_green"] as const;
const DESIRED_DEPTHS = ["natural", "dark"] as const;

function hasMarkerEligibleEyes(inputs: SelectionInputs) {
  return inputs.eye_color === "brown" || inputs.eye_color === "copper_brown";
}

function matchesH007Form(inputs: SelectionInputs) {
  return inputs.pigment_type === "hybrid" &&
    (inputs.skin_depth === "olive" || inputs.skin_depth === "tan") &&
    inputs.hair_color === "black" && hasMarkerEligibleEyes(inputs) &&
    (inputs.iris_marker === "green" || inputs.iris_marker === "yellow_green");
}

function hasMixProfile(inputs: SelectionInputs) {
  const baseComplete = inputs.pigment_type != null && inputs.skin_depth != null && inputs.skin_tone != null &&
    inputs.old_pmu_color != null && inputs.eye_color != null && inputs.hair_color != null &&
    inputs.has_couperose != null;
  const markerComplete = !hasMarkerEligibleEyes(inputs) || inputs.iris_marker != null;
  const depthComplete = !matchesH007Form(inputs) || inputs.desired_depth != null;
  return baseComplete && markerComplete && depthComplete;
}

function initialWizardStep(cache?: SelectionWizardCache | null) {
  if (!cache) return 1;
  if (cache.result) return TOTAL_STEPS;

  const requested = Math.min(Math.max(cache.step ?? 1, 1), TOTAL_STEPS);
  if (requested >= 2 && (!cache.preview || !hasMixProfile(cache.inputs))) return 1;
  if (requested >= 3 && cache.inputs.selected_mix_index == null) return 2;
  if (requested >= 5 && !cache.inputs.layup_choice) return 3;
  return requested;
}

type SchemeChoice = {
  key: string;
  titleKey: string;
  descriptionKey: string;
  lessonSlug: string;
};

const DEFINED_SCHEMES: SchemeChoice[] = [
  { key: "fast_fluffy", titleKey: "schemeFastFluffy", descriptionKey: "schemeFastFluffyDesc", lessonSlug: "schemes-fast-fluffy" },
  { key: "classic", titleKey: "schemeClassic", descriptionKey: "schemeClassicDesc", lessonSlug: "schemes-classic-gradient" },
  { key: "decorative_bottom", titleKey: "schemeDecorativeBottom", descriptionKey: "schemeDecorativeBottomDesc", lessonSlug: "schemes-dense-bottom" },
];
const GRAPHICS_SCHEMES: SchemeChoice[] = [
  { key: "decorative_graphics", titleKey: "schemeDecorativeGraphics", descriptionKey: "schemeDecorativeGraphicsDesc", lessonSlug: "schemes-graphics-sectors" },
  { key: "soft_graphics", titleKey: "schemeSoftGraphics", descriptionKey: "schemeSoftGraphicsDesc", lessonSlug: "schemes-graphics" },
];
const DIRECT_SCHEMES: SchemeChoice[] = [
  { key: "borderless", titleKey: "schemeBorderless", descriptionKey: "schemeBorderlessDesc", lessonSlug: "schemes-dense-center" },
  { key: "lady_plus", titleKey: "schemeLadyPlus", descriptionKey: "schemeLadyPlusDesc", lessonSlug: "schemes-brows-50plus" },
  { key: "asian", titleKey: "schemeAsian", descriptionKey: "schemeAsianDesc", lessonSlug: "schemes-asian-layup" },
];

function cap(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function densityKey(value: string) {
  if (value === "dense_porous") return "densityDensePorous";
  if (value === "thin_dry") return "densityThinDry";
  return "density" + cap(value);
}

function SummaryPanel({ inputs, compact }: { inputs: SelectionInputs; compact?: boolean }) {
  const { t } = useTranslation();
  const L = (key: string) => t(`selection.${key}`);
  const rows = [
    { label: L("s1Title"), val: inputs.pigment_type ? L(inputs.pigment_type === "mineral" ? "pigmentMineral" : "pigmentHybrid") : null },
    { label: L("skinDepth"), val: inputs.skin_depth ? L("depth" + cap(inputs.skin_depth)) : null },
    { label: L("skinTone"), val: inputs.skin_tone ? L("skinTone" + cap(inputs.skin_tone)) : null },
    { label: L("oldPermanent"), val: inputs.old_pmu_color ? L("pmu_" + inputs.old_pmu_color) : null, swatch: inputs.old_pmu_color ? PMU_SWATCH[inputs.old_pmu_color] : undefined },
    { label: L("eyeColor"), val: inputs.eye_color ? L("eye_" + inputs.eye_color) : null, swatch: inputs.eye_color ? EYE_SWATCH[inputs.eye_color] : undefined },
    { label: L("irisMarker"), val: inputs.iris_marker ? L("irisMarker_" + inputs.iris_marker) : null },
    { label: L("hairColor"), val: inputs.hair_color ? L("hair" + cap(inputs.hair_color)) : null },
    { label: L("desiredDepth"), val: inputs.desired_depth ? L("desiredDepth_" + inputs.desired_depth) : null },
    { label: L("couperose"), val: inputs.has_couperose == null ? null : L(inputs.has_couperose ? "yes" : "no") },
    { label: L("chosenScheme"), val: inputs.layup_choice ? L(inputs.layup_choice === "skip" ? "schemeSkipped" : `schemeChoice_${inputs.layup_choice}`) : null },
    { label: L("skinDensity"), val: inputs.skin_density ? L(densityKey(inputs.skin_density)) : null },
  ];
  const visible = compact ? rows.filter((row) => row.val) : rows;

  return (
    <div className={compact ? "" : "glass glass-lg"} style={compact ? {} : { padding: 20 }}>
      {!compact && <SectionHead icon="scan">{t("selection.summary")}</SectionHead>}
      <div className="stack" style={{ "--gap": "0px" } as React.CSSProperties}>
        {visible.map((row, index) => (
          <div key={index} className="between" style={{ padding: "10px 0", borderTop: index ? "1px solid var(--border)" : "none" }}>
            <span className="t-sm muted" style={{ fontWeight: 600 }}>{row.label}</span>
            <span className="t-sm row" style={{ gap: 7, fontWeight: 700, textAlign: "right" }}>
              {row.swatch && <span className="pill__swatch" style={{ background: row.swatch, width: 15, height: 15 }} />}
              {row.val ? <span>{row.val}</span> : <span className="muted-2">{t("selection.notSet")}</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MixChoices({ result, selected, onSelect }: {
  result: SelectionResult;
  selected?: number;
  onSelect: (index: number) => void;
}) {
  const { t } = useTranslation();
  const covers = result.color_variants.filter((variant) => variant.kind === "coverup");
  const mixes = result.color_variants.filter((variant) => variant.kind !== "coverup");
  const roleKeys = ["mixNeutral", "mixWarm", "mixLight"];
  const reasonKeys = ["mixNeutralWhy", "mixWarmWhy", "mixLightWhy"];

  return (
    <div className="stack" style={{ "--gap": "18px" } as React.CSSProperties}>
      {(result.applied_rules ?? []).map((rule) => (
        <Banner key={rule.rule_id} kind="success">
          <strong>{rule.rule_id} · {rule.title}</strong><br />{rule.explanation}
        </Banner>
      ))}
      {(result.warnings ?? []).map((warning, index) => (
        <Banner key={`warning-${index}`} kind="error">{warning}</Banner>
      ))}
      {result.finish_pass && (
        <Banner kind="info">
          <strong>{result.finish_pass.title}</strong><br />{result.finish_pass.instruction}
        </Banner>
      )}
      {covers.length > 0 && (
        <div className="stack" style={{ "--gap": "10px" } as React.CSSProperties}>
          <Banner kind="info">{t("selection.coverupFirstHint")}</Banner>
          {covers.map((variant, index) => (
            <MixCard key={`cover-${index}`} v={variant} badge={t("selection.badgeCoverup")} accent />
          ))}
        </div>
      )}
      {mixes.map((variant, index) => (
        <div key={`mix-${index}`} className="stack" style={{ "--gap": "9px" } as React.CSSProperties}>
          <div className="glass glass-1" style={{ padding: "12px 14px", borderRadius: 14 }}>
            <div className="t-h">{index + 1}. {t(`selection.${roleKeys[index]}`)}</div>
            <p className="t-sm muted" style={{ marginTop: 4, lineHeight: 1.45 }}>{t(`selection.${reasonKeys[index]}`)}</p>
            {index === 0 && result.pigment_type === "hybrid" && (
              <p className="t-xs" style={{ marginTop: 7, color: "var(--warn-text, #a05a00)", fontWeight: 700, lineHeight: 1.45 }}>
                {t("selection.hybridBrightPressure")}
              </p>
            )}
          </div>
          <MixCard v={variant} badge={selected === index ? t("selection.mixSelected") : t(`selection.${roleKeys[index]}`)} accent={selected === index} />
          <button type="button" className={selected === index ? "btn btn-primary btn-block" : "btn btn-glass btn-block"} onClick={() => onSelect(index)}>
            <Icon name={selected === index ? "check" : "droplet"} size={17} />
            {selected === index ? t("selection.mixSelected") : t("selection.chooseThisMix")}
          </button>
        </div>
      ))}
    </div>
  );
}

function SchemeCard({ option, selected, onClick, nested = false }: { option: SchemeChoice; selected: boolean; onClick: () => void; nested?: boolean }) {
  const { t } = useTranslation();
  return (
    <button type="button" onClick={onClick} className={nested ? "scheme-sub" + (selected ? " is-selected" : "") : "glass glass-lg"}
      aria-pressed={selected}
      style={nested ? undefined : { width: "100%", padding: 16, color: "inherit", textAlign: "left", cursor: "pointer", border: selected ? "1px solid var(--accent-fill)" : undefined }}>
      <div className="row between" style={{ alignItems: "flex-start", gap: 10 }}>
        <div>
          <div className="t-h">{t(`selection.${option.titleKey}`)}</div>
          <p className="t-sm muted" style={{ marginTop: 5, lineHeight: 1.45 }}>{t(`selection.${option.descriptionKey}`)}</p>
        </div>
        <span style={{ flex: "none", display: "grid", placeItems: "center", color: selected ? "var(--violet-text)" : "var(--text-2)" }}>
          <Icon name={selected ? "check" : "chevronRight"} size={18} />
        </span>
      </div>
    </button>
  );
}

/**
 * A family of schemes that opens in place. The options slide in under the
 * header, indented on a rail and tinted differently, so it is obvious they are
 * the choices INSIDE this group rather than more groups. Always rendered (the
 * open/close height animates); `inert` keeps a closed group out of tab order.
 */
function SchemeGroup({
  title, open, onToggle, hasSelection, children,
}: { title: string; open: boolean; onToggle: () => void; hasSelection: boolean; children: React.ReactNode }) {
  const { t } = useTranslation();
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => { bodyRef.current?.toggleAttribute("inert", !open); }, [open]);
  return (
    <div className={"scheme-group" + (open ? " is-open" : "")}>
      <button type="button" className="scheme-group__head" aria-expanded={open} onClick={onToggle}>
        <span style={{ minWidth: 0 }}>
          <span className="t-h" style={{ display: "block" }}>{title}</span>
          <span className="t-xs muted-2">
            {open ? t("selection.schemeGroupOpen") : hasSelection ? t("selection.schemeGroupChosen") : t("selection.schemeGroupClosed")}
          </span>
        </span>
        <span className="scheme-group__chev"><Icon name="chevronDown" size={18} /></span>
      </button>
      <div ref={bodyRef} className="scheme-group__body">
        <div className="scheme-group__clip">
          <div className="scheme-group__list">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function SelectionWizard({ token, onExit, theme, onThemeChange, onOpenLesson, onOpenPlans, cached, onCache }: Props) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(() => initialWizardStep(cached));
  const [inputs, setInputs] = useState<SelectionInputs>(cached?.inputs ?? {});
  const [preview, setPreview] = useState<SelectionResult | null>(cached?.preview ?? null);
  const [result, setResult] = useState<SelectionResult | null>(cached?.result ?? null);
  const [schemeGroup, setSchemeGroup] = useState<string | null>(cached?.schemeGroup ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<SelectionQuota | null>(null);

  useEffect(() => {
    fetchSelectionQuota(token).then(setQuota).catch(() => setQuota(null));
  }, [token]);

  // Each wizard step replaces content inside the same overflow container, so
  // browsers otherwise preserve the previous step's scroll offset. Reset both
  // the app container and the document for Telegram WebView/browser parity.
  useLayoutEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [step]);

  const limitReached = !!quota && !quota.unlimited && quota.remaining <= 0;
  const set = (patch: Partial<SelectionInputs>) => setInputs((current) => ({ ...current, ...patch }));

  const stepComplete = (value: number) => {
    if (value === 1) return hasMixProfile(inputs);
    if (value === 2) return preview != null && inputs.selected_mix_index != null;
    if (value === 3) return preview != null && inputs.selected_mix_index != null;
    if (value === 4) return !!inputs.layup_choice && inputs.layup_choice !== "skip";
    if (value === 5) {
      return hasMixProfile(inputs) && preview != null && inputs.selected_mix_index != null &&
        !!inputs.layup_choice && inputs.skin_density != null;
    }
    return false;
  };
  const canAdvance = stepComplete(step);

  const handleError = (cause: unknown) => {
    const message = cause instanceof Error ? cause.message : "";
    if (message.includes("SELECTION_LIMIT_REACHED") || message.includes("403")) {
      setQuota((current) => (current ? { ...current, remaining: 0 } : current));
      setError(t("selection.limitReached"));
    } else setError(message || t("common.error"));
  };

  const createPreview = async () => {
    if (limitReached) return;
    setLoading(true);
    setError(null);
    try {
      const previewInputs = {
        ...inputs,
        selected_mix_index: undefined,
        layup_choice: undefined,
        skin_density: undefined,
      };
      const nextPreview = await previewSelection(previewInputs, token);
      // A preview of a new client profile uses one free calculation.
      fetchSelectionQuota(token).then(setQuota).catch(() => {});
      setPreview(nextPreview);
      setInputs(previewInputs);
      setStep(2);
    } catch (cause) { handleError(cause); }
    finally { setLoading(false); }
  };

  const createFinalReport = async () => {
    if (limitReached || !stepComplete(5)) return;
    setLoading(true);
    setError(null);
    try {
      const nextResult = await recommendSelection(inputs, token);
      setResult(nextResult);
      onCache?.({ result: nextResult, inputs });
      fetchSelectionQuota(token).then(setQuota).catch(() => {});
    } catch (cause) { handleError(cause); }
    finally { setLoading(false); }
  };

  const goNext = async () => {
    if (!stepComplete(step)) return;
    if (step === 1) return createPreview();
    if (step === 5) return createFinalReport();
    setError(null);
    setStep(step + 1);
  };

  const skipScheme = () => {
    set({ layup_choice: "skip", skin_density: undefined });
    setSchemeGroup(null);
    setStep(5);
  };

  const goBack = () => {
    if (result) { setResult(null); return; }
    if (step === 5 && inputs.layup_choice === "skip") { setStep(3); return; }
    if (step > 1) setStep(step - 1);
    else onExit();
  };

  const restart = () => {
    setStep(1); setInputs({}); setPreview(null); setResult(null); setSchemeGroup(null); setError(null); onCache?.(null);
  };

  if (result) {
    return <ResultScreen result={result} onExit={onExit} onHome={onExit} onRestart={restart} onOpenLesson={onOpenLesson} onOpenPlans={onOpenPlans} clientName={inputs.client_name} theme={theme} onThemeChange={onThemeChange} />;
  }

  const stepIcons = ["", "flask", "droplet", "sparkles", "layers", "brush"];
  const stepTitles = ["", t("selection.s2Title"), t("selection.mixReady"), t("selection.schemeHelpTitle"), t("selection.s4Title"), t("selection.s3Title")];

  const stepContent = (
    <div key={step} className="anim-fade stack" style={{ "--gap": "20px" } as React.CSSProperties}>
      {step === 1 && (
        <>
          {quota && !quota.unlimited && (limitReached
            ? <Banner kind="error">{t("selection.limitReached")}</Banner>
            : <Banner kind="info">{t("selection.quotaLeft")}: {quota.remaining} / {quota.limit}</Banner>)}
          <ChoiceRow label={t("selection.s1Title")} value={inputs.pigment_type} onChange={(value) => set({ pigment_type: value, desired_depth: undefined })}
            options={[{ val: "mineral", label: t("selection.pigmentMineral") }, { val: "hybrid", label: t("selection.pigmentHybrid") }]} />
          <ChoiceRow label={t("selection.skinDepth")} hint={t("selection.zoomHint")} value={inputs.skin_depth} onChange={(value) => set({ skin_depth: value, desired_depth: undefined })}
            options={["fair", "light", "olive", "tan", "dark"].map((value) => ({ val: value, label: t(`selection.depth${cap(value)}`), image: SKIN_IMG[value], imageLarge: SKIN_IMG[value].replace(".jpg", "_lg.jpg") }))} />
          <ChoiceRow label={t("selection.skinTone")} value={inputs.skin_tone} onChange={(value) => set({ skin_tone: value })}
            options={["cold", "neutral"].map((value) => ({ val: value, label: t(`selection.skinTone${cap(value)}`) }))} />
          <ChoiceRow label={t("selection.oldPermanent")} value={inputs.old_pmu_color} onChange={(value) => set({ old_pmu_color: value, has_old_permanent: value !== "none" })}
            options={PMU_COLORS.map((value) => ({ val: value, label: t(`selection.pmu_${value}`), swatch: PMU_SWATCH[value] }))} />
          <ChoiceRow label={t("selection.eyeColor")} hint={t("selection.zoomHint")} value={inputs.eye_color} onChange={(value) => set({
            eye_color: value,
            iris_marker: value === "brown" || value === "copper_brown" ? inputs.iris_marker : undefined,
            desired_depth: undefined,
          })}
            options={Object.keys(EYE_SWATCH).map((value) => ({ val: value, label: t(`selection.eye_${value}`), swatch: EYE_SWATCH[value], image: EYE_IMG[value], imageLarge: EYE_IMG[value].replace(".jpg", "_lg.jpg") }))} />
          {hasMarkerEligibleEyes(inputs) && (
            <ChoiceRow label={t("selection.irisMarker")} hint={t("selection.irisMarkerHint")} value={inputs.iris_marker} onChange={(value) => set({ iris_marker: value, desired_depth: undefined })}
              options={IRIS_MARKERS.map((value) => ({ val: value, label: t(`selection.irisMarker_${value}`) }))} />
          )}
          <ChoiceRow label={t("selection.hairColor")} hint={t("selection.zoomHint")} value={inputs.hair_color} onChange={(value) => set({ hair_color: value, desired_depth: undefined })}
            options={["red", "blonde", "brown", "chestnut", "black"].map((value) => ({ val: value, label: t(`selection.hair${cap(value)}`), image: HAIR_IMG[value], imageLarge: HAIR_IMG[value].replace(".jpg", "_lg.jpg") }))} />
          {matchesH007Form(inputs) && (
            <ChoiceRow label={t("selection.desiredDepth")} hint={t("selection.desiredDepthHint")} value={inputs.desired_depth} onChange={(value) => set({ desired_depth: value })}
              options={DESIRED_DEPTHS.map((value) => ({ val: value, label: t(`selection.desiredDepth_${value}`) }))} />
          )}
          <ChoiceRow label={t("selection.couperose")} hint={t("selection.couperoseHint")} value={inputs.has_couperose} onChange={(value) => set({ has_couperose: value })}
            options={[{ val: true, label: t("selection.yes"), image: COUPEROSE_IMG, imageLarge: COUPEROSE_IMG.replace(".jpg", "_lg.jpg") }, { val: false, label: t("selection.no") }]} />
        </>
      )}

      {step === 2 && preview && <MixChoices result={preview} selected={inputs.selected_mix_index} onSelect={(index) => set({ selected_mix_index: index, layup_choice: undefined, skin_density: undefined })} />}

      {step === 3 && (
        <div className="stack" style={{ "--gap": "14px" } as React.CSSProperties}>
          <p className="t-body muted">{t("selection.schemeHelpText")}</p>
        </div>
      )}

      {step === 4 && (
        <div className="stack" style={{ "--gap": "12px" } as React.CSSProperties}>
          <p className="t-sm muted">{t("selection.manualSchemeHint")}</p>
          <SchemeGroup title={t("selection.schemeGroupDefined")} open={schemeGroup === "defined"}
            hasSelection={DEFINED_SCHEMES.some((o) => o.key === inputs.layup_choice)}
            onToggle={() => setSchemeGroup(schemeGroup === "defined" ? null : "defined")}>
            {DEFINED_SCHEMES.map((option) => <SchemeCard key={option.key} nested option={option} selected={inputs.layup_choice === option.key} onClick={() => set({ layup_choice: option.key, skin_density: undefined })} />)}
          </SchemeGroup>
          <SchemeGroup title={t("selection.schemeGroupGraphics")} open={schemeGroup === "graphics"}
            hasSelection={GRAPHICS_SCHEMES.some((o) => o.key === inputs.layup_choice)}
            onToggle={() => setSchemeGroup(schemeGroup === "graphics" ? null : "graphics")}>
            {GRAPHICS_SCHEMES.map((option) => <SchemeCard key={option.key} nested option={option} selected={inputs.layup_choice === option.key} onClick={() => set({ layup_choice: option.key, skin_density: undefined })} />)}
          </SchemeGroup>
          {DIRECT_SCHEMES.map((option) => <SchemeCard key={option.key} option={option} selected={inputs.layup_choice === option.key} onClick={() => { set({ layup_choice: option.key, skin_density: undefined }); setSchemeGroup(null); }} />)}
        </div>
      )}

      {step === 5 && (
        <div className="stack" style={{ "--gap": "14px" } as React.CSSProperties}>
          <ChoiceRow label={t("selection.skinDensity")} value={inputs.skin_density} onChange={(value) => set({ skin_density: value })}
            options={["dense_porous", "thin_dry"].map((value) => ({ val: value, label: t(`selection.${densityKey(value)}`) }))} />
          <div>
            <p className="t-sm muted" style={{ marginBottom: 9 }}>{t("selection.clientName")}</p>
            <Field icon="user" name="client_name" value={inputs.client_name ?? ""} onChange={(value) => set({ client_name: value })} placeholder={t("selection.clientNamePlaceholder")} />
            <p className="t-xs muted-2" style={{ marginTop: 6 }}>{t("selection.clientNameHint")}</p>
          </div>
          <SummaryPanel inputs={inputs} compact />
        </div>
      )}
      {error && <Banner kind="error">{error}</Banner>}
    </div>
  );

  const buttonKey = step === 1 ? "pickMix" : step === 2 ? "continueToScheme" : step === 3 ? "chooseScheme" : step === 4 ? "pickCartridge" : "fullReport";
  const requiredKey = step === 2 ? "mixRequired" : step === 4 ? "schemeRequired" : step === 5 ? "cartridgeRequired" : "required";

  return (
    <div ref={scrollRef} className="beeapp__scroll" style={{ display: "flex", flexDirection: "column" }}>
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={goBack} title={t("selection.title")} />
      <div className="wrap" style={{ flex: 1, paddingTop: 6, paddingBottom: 0 }}>
        <div className="two-pane">
          <div>
            <div style={{ marginBottom: 18 }}>
              <div className="between" style={{ marginBottom: 10 }}>
                <span className="eyebrow">{t("selection.step")} {step} {t("selection.of")} {TOTAL_STEPS}</span>
                <span className="t-xs muted-2">{Math.round((step / TOTAL_STEPS) * 100)}%</span>
              </div>
              <div className="steps">{Array.from({ length: TOTAL_STEPS }).map((_, index) => <span key={index} className={`steps__dot${index + 1 < step ? " is-done" : index + 1 === step ? " is-active" : ""}`} />)}</div>
            </div>
            <div className="row" style={{ gap: 10, marginBottom: 18 }}>
              <span style={{ display: "grid", placeItems: "center", width: 40, height: 40, borderRadius: 12, background: "var(--accent-tint)", color: "var(--accent-text)", flex: "none" }}><Icon name={stepIcons[step]} size={22} /></span>
              <h1 className="t-title">{stepTitles[step]}</h1>
            </div>
            {stepContent}
          </div>
          <aside className="two-pane__side"><SummaryPanel inputs={inputs} /></aside>
        </div>
      </div>
      <div className="sticky-foot">
        <div className="content-cap two-pane" style={{ alignItems: "stretch" }}>
          <div>
            {!canAdvance && !loading && <p className="t-xs muted-2" style={{ textAlign: "center", marginBottom: 8 }}>{t(`selection.${requiredKey}`)}</p>}
            <div className="row wiz-foot-row" style={{ gap: 10 }}>
              <button className="btn btn-glass wiz-back" onClick={goBack} style={{ flex: "0 0 auto", paddingInline: 18 }} aria-label={t("selection.back")}><Icon name="chevronLeft" size={18} /> <span className="wiz-back__label">{t("selection.back")}</span></button>
              {step === 3 && (
                <button className="btn btn-glass" onClick={skipScheme} style={{ flex: "0 0 auto", paddingInline: 18 }}>
                  {t("selection.skipScheme")}
                </button>
              )}
              <button className="btn btn-primary" onClick={goNext} disabled={loading || !canAdvance || ((step === 1 || step === 5) && limitReached)} style={{ flex: 1, minWidth: 0 }}>
                {loading ? <span className="spinner" /> : <>{t(`selection.${buttonKey}`)} <Icon name={step === 5 ? "sparkles" : "chevronRight"} size={18} /></>}
              </button>
            </div>
          </div>
          <div className="two-pane__side" aria-hidden />
        </div>
      </div>
    </div>
  );
}
